import {createStyles} from '@mantine/core';
import React, {ChangeEvent} from 'react';

import {AaNaturalAnalog} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AA_NATURAL_ANALOG_DISPLAY_NAME,
  AA_NATURAL_ANALOG_SELECT_OPTIONS,
} from 'molbio/aa-monomers/AaMonomerConstants';
import {SharedMonomerValidationErrors} from 'molbio/monomers/SharedMonomerConstants';
import {FONT_PROXIMA_13, FONT_PROXIMA_18} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {InputErrorMessageStyle, LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface AaMonomerManualCreationDefinePaneProps {
  name: string;
  onChangeName: (name: string) => void;
  symbol: string;
  onChangeSymbol: (symbol: string) => void;
  naturalAnalogue: AaNaturalAnalog;
  onChangeNaturalAnalogue: (naturalAnalogue: AaNaturalAnalog) => void;
  validationErrors: SharedMonomerValidationErrors;
}

export default function AaMonomerManualCreationDefinePane({
  name,
  onChangeName,
  symbol,
  onChangeSymbol,
  naturalAnalogue,
  onChangeNaturalAnalogue,
  validationErrors,
}: AaMonomerManualCreationDefinePaneProps): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <div
      className={classes.paneContainer}
      {...getTestProps({component: 'AaMonomerManualCreationDefinePane', element: 'root'})}
    >
      <div className={classes.header}>
        <span>Define new monomer settings</span>
      </div>
      <TextField
        className={classes.marginBottom16}
        value={name}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChangeName(e.target.value);
        }}
        label="Name"
        labelStyle={LABEL_STYLES.LABEL}
        isRequired={true}
        autoFocus={true}
        dataTestAttrs={{
          component: 'AaMonomerManualCreationDefinePane',
          element: 'nameInput',
        }}
      />
      <TextField
        className={classes.marginBottom16}
        value={symbol}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChangeSymbol(e.target.value);
        }}
        label="Symbol"
        labelStyle={LABEL_STYLES.LABEL}
        isRequired={true}
        hasError={validationErrors.symbol != null}
        error={
          validationErrors.symbol != null
            ? {
                message: validationErrors.symbol,
                style: InputErrorMessageStyle.INFO,
              }
            : null
        }
        dataTestAttrs={{
          component: 'AaMonomerManualCreationDefinePane',
          element: 'symbolInput',
        }}
      />
      <div className={cx(classes.horizontalFieldsSection, classes.marginBottom16)}>
        <div className={classes.flex1}>
          <div className={classes.textFieldSubheader}>
            <span>Natural analog</span>
          </div>
          <SelectDropdown
            menu={
              <SearchableMenu
                width={MenuWidth.LARGE}
                selectedValue={naturalAnalogue}
                optionDatas={AA_NATURAL_ANALOG_SELECT_OPTIONS}
                onSelect={(value) => {
                  onChangeNaturalAnalogue(value as AaNaturalAnalog);
                }}
              />
            }
            displayName={`${naturalAnalogue} (${AA_NATURAL_ANALOG_DISPLAY_NAME[naturalAnalogue]})`}
            dataTestAttrs={{
              component: 'AaMonomerManualCreationDefinePane',
              element: 'naturalAnalogDropdown',
            }}
          />
        </div>
      </div>
    </div>
  );
}

const useStyles = createStyles({
  paneContainer: {
    marginLeft: 40,
    marginRight: 40,
  },
  header: {
    ...FONT_PROXIMA_18,
    marginTop: 18,
    marginBottom: 12,
  },
  horizontalFieldsSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  marginBottom16: {
    marginBottom: 16,
  },
  textFieldSubheader: {
    fontWeight: 'bold',
    marginBottom: 4,
    ...FONT_PROXIMA_13,
  },
  flex1: {
    flex: 1,
  },
});
